import { useState, useRef, useCallback, type KeyboardEvent } from 'react';
import { PaperAirplaneIcon, StopIcon } from '@heroicons/react/24/solid';

export default function ChatInput({
  onSend,
  onStop,
  isLoading,
}: {
  onSend: (text: string) => void;
  onStop: () => void;
  isLoading: boolean;
}) {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    onSend(trimmed);
    setText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [text, isLoading, onSend]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 96) + 'px'; // max ~4 lines
    }
  };

  return (
    <div className="border-t border-gray-200 p-2 flex items-end gap-2">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => { setText(e.target.value); handleInput(); }}
        onKeyDown={handleKeyDown}
        placeholder="Ask about your EEP data..."
        rows={1}
        className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-eep-navy/50 focus:border-eep-navy/50"
        style={{ maxHeight: '96px' }}
      />
      {isLoading ? (
        <button
          onClick={onStop}
          className="shrink-0 w-8 h-8 rounded-lg bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
          title="Stop"
        >
          <StopIcon className="w-4 h-4" />
        </button>
      ) : (
        <button
          onClick={handleSend}
          disabled={!text.trim()}
          className="shrink-0 w-8 h-8 rounded-lg bg-eep-navy hover:bg-eep-navy-light disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
          title="Send"
        >
          <PaperAirplaneIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
